import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Unplug, Clock, Settings, X } from 'lucide-react';
import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

/**
 * L'autorisation d'afficher des notifications, telle que le navigateur la voit.
 *
 * Elle ne se déduit pas de la case cochée : la case vit en base, l'autorisation vit dans le
 * navigateur, et les deux se contredisent sans se le dire. Le même compte peut être autorisé
 * au bureau et bloqué à la maison — d'où une lecture à chaque affichage, jamais un stockage.
 *
 * Quatre états, dont trois demandent une conduite différente :
 * - `absente` : navigateur sans l'API. Rien à proposer.
 * - `default` : jamais demandée. Un clic suffit.
 * - `granted` : la case dit vrai.
 * - `denied` : refusée. `requestPermission()` renvoie « denied » sans rien afficher ; seuls
 *   les réglages du navigateur débloquent, il faut donc le dire au lieu de le retenter.
 */
const lirePermission = () => (
  typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'absente'
);

const sectionStyle = {
  marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #f3f4f6'
};

const titreStyle = { fontSize: '14px', fontWeight: 600, color: '#111827', margin: '0 0 4px 0' };

const aideStyle = { fontSize: '12px', color: '#6b7280', margin: '0 0 14px 0', lineHeight: 1.5 };

const caseStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  fontSize: '13px', color: '#374151', marginBottom: '8px', cursor: 'pointer'
};

/**
 * Alertes de compte inaccessible — carte de la vue Notifications.
 *
 * ## Pourquoi une carte, et plus un bandeau
 *
 * L'information vivait en bandeau rouge en tête de dashboard. Elle ne prévenait donc que qui
 * ouvrait un écran — or c'est justement le compte qu'on n'ouvre plus qui perd son accès sans
 * qu'on s'en aperçoive. Elle s'affichait de surcroît hors de son contexte, un bandeau
 * « compte Google Ads inaccessible » venant coiffer le dashboard ChatGPT Ads d'un autre
 * client. Un accès coupé est un événement daté : sa place est ici.
 *
 * ## Les canaux sont dans la modale, comme partout ailleurs
 *
 * La carte ne porte qu'un interrupteur et ce que fait l'outil ; le détail des canaux tient
 * derrière « Paramétrer ». Trois cartes voisines suivent déjà cette règle, et des cases à
 * cocher posées à même la carte y faisaient une exception visible.
 *
 * ## Actif par défaut
 *
 * Perdre l'accès à un compte n'est pas un réglage qu'on choisit, c'est une panne qu'on subit.
 * Le premier balayage est en revanche silencieux : mettre l'outil en service sur un parc déjà
 * dégradé ne doit pas envoyer une salve d'alertes pour des coupures parfois anciennes.
 */
const AccountAccessAlertsCard = ({ token }) => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [permission, setPermission] = useState(lirePermission);

  const charger = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts/access-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data?.success) setPrefs(data.preferences);
    } catch {
      // Réglages indisponibles : la carte ne s'affiche pas plutôt que d'afficher un état faux.
    }
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  /**
   * L'autorisation peut changer hors de Kokpit — réglages du navigateur, autre onglet. La
   * relire au retour évite d'afficher un avertissement que l'utilisateur vient de lever.
   */
  useEffect(() => {
    const relire = () => setPermission(lirePermission());
    document.addEventListener('visibilitychange', relire);
    window.addEventListener('focus', relire);
    return () => {
      document.removeEventListener('visibilitychange', relire);
      window.removeEventListener('focus', relire);
    };
  }, []);

  const demanderPermission = async () => {
    // « denied » ne se retente pas : le navigateur répond sans rien afficher.
    if (lirePermission() !== 'default') return lirePermission();
    try { await Notification.requestPermission(); } catch { /* refus : l'e-mail suffira */ }
    const etat = lirePermission();
    setPermission(etat);
    return etat;
  };

  const enregistrer = async (patch) => {
    // L'état local part en avant : une bascule doit répondre au clic, pas au réseau.
    setPrefs(actuelles => ({ ...actuelles, ...patch }));
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/alerts/access-preferences`, patch, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data?.success) setPrefs(data.preferences);
    } catch {
      // L'écriture a échoué : on relit plutôt que de laisser une case mentir.
      charger();
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;

  return (
    <>
      <div className="notification-card">
        <div className="notification-card-header">
          <Unplug size={24} />
          <h2>Alertes de compte inaccessible</h2>
          {prefs.isActive && (
            <button
              className="config-btn"
              onClick={() => setShowModal(true)}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 14px', background: '#1a1a1a', color: 'white', border: 'none',
                borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
              }}
            >
              <Settings size={16} />
              Paramétrer
            </button>
          )}
        </div>

        <div className="notification-card-body">
          <div className="notification-option">
            <div className="notification-option-info">
              <h3>Prévenir quand un accès est coupé</h3>
              <p>
                Sonde chaque jour tous vos comptes connectés — régies, analytics, Merchant
                Center, Search Console — et vous prévient dès que l'un d'eux cesse de
                répondre : accès révoqué par le client, connexion expirée, compte supprimé.
              </p>
              <div className="notification-schedule">
                <Clock size={16} />
                <span>Vérification quotidienne à 8h00 — un seul message par coupure</span>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={prefs.isActive}
                onChange={(e) => enregistrer({ isActive: e.target.checked })}
                disabled={saving}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {showModal && ReactDOM.createPortal(
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', width: '100%', maxWidth: '620px',
              maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Paramètres des alertes d'accès</h2>
              <button onClick={() => setShowModal(false)} className="btn-icon"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto' }}>
              <section style={{ ...sectionStyle, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
                <h3 style={titreStyle}>Canaux</h3>
                <p style={aideStyle}>
                  Une coupure d'accès ne se répète pas : un seul message part, et l'alerte se
                  referme d'elle-même si l'accès est rendu.
                </p>

                <label style={caseStyle}>
                  <input
                    type="checkbox"
                    checked={prefs.emailEnabled}
                    onChange={(e) => enregistrer({ emailEnabled: e.target.checked })}
                    disabled={saving}
                  />
                  <span>E-mail</span>
                </label>
                <label style={caseStyle}>
                  <input
                    type="checkbox"
                    checked={prefs.popinEnabled}
                    onChange={(e) => enregistrer({ popinEnabled: e.target.checked })}
                    disabled={saving}
                  />
                  <span>Pop-in dans Kokpit</span>
                </label>
                <label style={caseStyle}>
                  <input
                    type="checkbox"
                    checked={prefs.browserEnabled}
                    onChange={async (e) => {
                      // La valeur se lit **avant** l'attente. La case est contrôlée : pendant
                      // que le navigateur affiche sa demande, React a déjà remis le DOM sur
                      // l'ancienne valeur, et `e.target.checked` relu ensuite renvoie le
                      // contraire du clic — la case redevenait impossible à cocher.
                      const actif = e.target.checked;
                      // Le clic sur la case est le geste qui exprime l'intention, et c'est
                      // aussi le seul moment où le navigateur accepte d'afficher sa demande.
                      if (actif) await demanderPermission();
                      enregistrer({ browserEnabled: actif });
                    }}
                    disabled={saving}
                  />
                  <span>Notification système</span>
                </label>

                {/* Chaque canal est nommé par le moment où il agit : c'est ce qui rend la
                    combinaison lisible sans l'expliquer. */}
                <p style={{ ...aideStyle, margin: '6px 0 0 26px' }}>
                  La pop-in vous prévient pendant que vous êtes dans Kokpit, la notification
                  système pendant que vous êtes ailleurs, et l'e-mail quand le navigateur est
                  fermé.
                </p>

                {/* L'état de l'autorisation, quand il contredit la case. La cloche, elle,
                    continue de lister les alertes dans tous les cas : seule la bulle du
                    système est en jeu ici. */}
                {prefs.browserEnabled && permission !== 'granted' && (
                  <div style={{
                    margin: '8px 0 0 26px', padding: '10px 12px', borderRadius: '6px',
                    backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                    fontSize: '12px', color: '#92400e', lineHeight: 1.5
                  }}>
                    {permission === 'default' && (
                      <>
                        Votre navigateur n'a pas encore autorisé les notifications système :
                        vous ne serez pas prévenu des coupures survenues pendant que Kokpit est
                        en arrière-plan.{' '}
                        {prefs.popinEnabled && 'La pop-in vous les montrera à votre retour.'}
                        <button
                          onClick={demanderPermission}
                          style={{
                            display: 'block', marginTop: '8px', padding: '6px 12px',
                            border: '1px solid #d97706', borderRadius: '6px',
                            backgroundColor: 'white', color: '#92400e',
                            fontSize: '12px', fontWeight: 600, cursor: 'pointer'
                          }}
                        >
                          Autoriser les notifications
                        </button>
                      </>
                    )}
                    {permission === 'denied' && (
                      <>
                        Les notifications système sont bloquées pour Kokpit dans ce navigateur.
                        {prefs.popinEnabled
                          ? " Une coupure survenue en votre absence s'affichera à votre retour sur l'onglet ; pour être prévenu sur le moment, autorisez"
                          : ' Pour être prévenu des coupures survenues en votre absence, autorisez'}
                        {' '}les notifications dans les réglages de site — l'icône à gauche de
                        l'adresse.
                      </>
                    )}
                    {permission === 'absente' && (
                      <>
                        Ce navigateur ne sait pas afficher de notifications système.
                        {prefs.popinEnabled
                          ? " La pop-in et l'e-mail couvrent le reste."
                          : " L'e-mail reste le seul canal quand Kokpit n'est pas au premier plan."}
                      </>
                    )}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default AccountAccessAlertsCard;
